const API_BASE_URL = typeof import.meta.env.VITE_API_URL === 'string'
  ? import.meta.env.VITE_API_URL
  : 'http://localhost:3000';

export interface RequestOptions extends RequestInit {
  params?: Record<string, string | number | boolean>;
}

export class ApiError extends Error {
  status: number;
  body: any;

  constructor(status: number, message: string, body: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/**
 * Cliente HTTP base para realizar peticiones tipadas al backend
 */
export const apiClient = {
  async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    const { params, headers, ...customOptions } = options;

    // Construir URL con query params si se proporcionan
    let url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    if (params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        searchParams.append(key, String(value));
      }
      url += `?${searchParams.toString()}`;
    }

    // Configurar cabeceras por defecto
    const requestHeaders = new Headers(headers);
    if (!requestHeaders.has('Content-Type') && !(customOptions.body instanceof FormData)) {
      requestHeaders.set('Content-Type', 'application/json');
    }

    // Inyectar JWT Token desde localStorage si existe
    const token = localStorage.getItem('discipline_token');
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`);
    }

    const config: RequestInit = {
      ...customOptions,
      headers: requestHeaders,
    };

    try {
      const response = await fetch(url, config);

      let responseData: any = null;
      const contentType = response.headers.get('Content-Type');
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json();
      } else {
        responseData = await response.text();
      }

      if (!response.ok) {
        // Interceptar 401 Unauthorized y desloguear
        if (response.status === 401) {
          localStorage.removeItem('discipline_token');
          localStorage.removeItem('discipline_user');
          
          // Solo redirigir si no estamos ya en la pantalla de login
          if (!window.location.hash.includes('#/login')) {
            window.location.hash = '#/login?error=session_expired';
            // Disparar evento para actualizar barra de navegación o UI
            window.dispatchEvent(new Event('auth_change'));
          }
        }
        
        throw new ApiError(
          response.status,
          responseData?.message || `Error de petición (${response.status})`,
          responseData
        );
      }

      return responseData as T;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 'Error de conexión con el servidor', error);
    }
  },

  get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  },

  post<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  put<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  patch<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  },
};
